import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../GlobalProvider/GlobalProvider";
import "./Login.css";
import { baseUrl } from "../../utils/helper";
import { loginDataType } from "../../utils/types";
import { useDispatch } from "react-redux";
import { setSelectedDoctor, setSelectedRole, setUserRoles } from "../../store/slices/authSlice";
import {
    MdMailOutline,
    MdLockOutline,
    MdVisibility,
    MdVisibilityOff,
    MdErrorOutline,
} from "react-icons/md";

const Login = () => {
    const navigate = useNavigate();
    const { setToken, setUserProfile } = useStore();
    const dispatch = useDispatch();

    const [loginData, setLoginData] = useState<loginDataType>({ email: "", password: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [touched, setTouched] = useState({ email: false, password: false });
    const [serverError, setServerError] = useState("");

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!loginData?.email) e.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(loginData.email)) e.email = "Enter a valid email address";
        if (!loginData?.password) e.password = "Password is required";
        else if (loginData.password.length < 6) e.password = "At least 6 characters required";
        return e;
    }, [loginData?.email, loginData?.password]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLoginData((prev: any) => ({ ...prev, [name]: value }));
        setServerError("");
    };

    const handleBlur = (field: "email" | "password") => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const handleLogin = async () => {
        setTouched({ email: true, password: true });
        if (errors.email || errors.password) return;

        try {
            setIsLoading(true);
            setServerError("");
            const res = await fetch(`${baseUrl}/login`, {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ email: loginData?.email, password: loginData?.password }),
            });
            const resData = await res.json();
            if (res.ok) {
                localStorage.setItem("isLogin", "1");
                localStorage.setItem("token", resData?.token);
                localStorage.setItem("profile_id", resData?.user?._id);
                localStorage.setItem("user", JSON.stringify(resData?.user));
                setToken(resData?.token);
                setUserProfile(resData?.user);
                dispatch(setUserRoles(resData?.userRoles));
                if (resData?.userRoles?.length > 0) {
                    dispatch(setSelectedRole(resData?.userRoles[0]));
                    if (resData?.userRoles[0]?.parentUsers?.length > 0) {
                        dispatch(setSelectedDoctor(resData?.userRoles[0]?.parentUsers[0]));
                    }
                }
                navigate("/");
            } else {
                setServerError(resData?.message || "Invalid email or password. Please try again.");
            }
        } catch {
            setServerError("Something went wrong. Please check your connection.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleLogin();
    };

    const fieldClass = (field: "email" | "password") =>
        `flex items-center gap-2.5 border rounded-xl px-3.5 transition-colors ${
            errors[field] && touched[field]
                ? "border-red-400 bg-red-50"
                : "border-gray-200 bg-gray-50 focus-within:border-[#12AB81] focus-within:bg-white"
        }`;

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center login-bg px-5 py-8">
            {/* Brand */}
            <div className="text-center mb-7">
                <p className="text-white text-2xl font-extrabold tracking-tight drop-shadow-md">Shafa Care</p>
                <p className="text-white/75 text-sm mt-1">Primary Healthcare Management</p>
            </div>

            {/* Card */}
            <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
                <div className="h-1 w-full bg-gradient-to-r from-[#12AB81] to-emerald-400" />

                <div className="px-6 py-6">
                    <p className="text-lg font-bold text-gray-800">Welcome back</p>
                    <p className="text-xs text-gray-400 mt-0.5 mb-5">Sign in to continue</p>

                    {/* Server error banner */}
                    {serverError && (
                        <div className="mb-4 flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl">
                            <MdErrorOutline className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                            <p className="text-red-600 text-xs leading-snug">{serverError}</p>
                        </div>
                    )}

                    {/* Email */}
                    <div className="mb-4">
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Email Address
                        </label>
                        <div className={fieldClass("email")}>
                            <MdMailOutline
                                size={18}
                                className={errors.email && touched.email ? "text-red-400 flex-shrink-0" : "text-gray-400 flex-shrink-0"}
                            />
                            <input
                                type="email"
                                name="email"
                                value={loginData?.email}
                                placeholder="you@example.com"
                                autoComplete="email"
                                className="flex-1 py-3 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                                onChange={handleChange}
                                onBlur={() => handleBlur("email")}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        {errors.email && touched.email && (
                            <p className="mt-1.5 flex items-center gap-1 text-xs text-red-500">
                                <MdErrorOutline size={13} />
                                {errors.email}
                            </p>
                        )}
                    </div>

                    {/* Password */}
                    <div className="mb-6">
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Password
                        </label>
                        <div className={fieldClass("password")}>
                            <MdLockOutline
                                size={18}
                                className={errors.password && touched.password ? "text-red-400 flex-shrink-0" : "text-gray-400 flex-shrink-0"}
                            />
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={loginData?.password}
                                placeholder="••••••••"
                                autoComplete="current-password"
                                className="flex-1 py-3 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                                onChange={handleChange}
                                onBlur={() => handleBlur("password")}
                                onKeyDown={handleKeyDown}
                            />
                            <button
                                type="button"
                                className="p-1 active:opacity-60 flex-shrink-0"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword
                                    ? <MdVisibilityOff size={18} className="text-gray-400" />
                                    : <MdVisibility size={18} className="text-gray-400" />
                                }
                            </button>
                        </div>
                        {errors.password && touched.password && (
                            <p className="mt-1.5 flex items-center gap-1 text-xs text-red-500">
                                <MdErrorOutline size={13} />
                                {errors.password}
                            </p>
                        )}
                    </div>

                    {/* Submit */}
                    <button
                        onClick={handleLogin}
                        disabled={isLoading}
                        className="w-full py-3.5 bg-[#12AB81] text-white rounded-xl font-semibold text-sm active:opacity-80 disabled:opacity-60 flex items-center justify-center gap-2 transition-opacity"
                    >
                        {isLoading ? (
                            <>
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Signing in…
                            </>
                        ) : (
                            "Sign In"
                        )}
                    </button>
                </div>
            </div>

            <p className="text-white/50 text-xs mt-6">© 2024 Shafa Care. All rights reserved.</p>
        </div>
    );
};

export default Login;
